import { Alert } from '@mui/material'
import { FC } from 'react'
import {
  DateTimeInput,
  FormDataConsumer,
  minValue,
  NumberInput,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  useGetList,
  useTranslate,
} from 'react-admin'

import { isWithinAnyStay, OPERATION_KINDS, PRODUCT_CLASSES, type SpecialEventType, validateTimeRange } from '@/data'
import { type ConnectionPoint, type EventRecord, MARPOL_ANNEXES } from '@/data/source'
import { fromDateTimeInput, toDateTimeInput } from '@/domain/dateTime'

import { useEventRecord, useScaleEvents } from './useScaleEvents'

const validateRange = (values: Record<string, unknown>): Record<string, string> => {
  const { startTime, endTime } = values
  if (typeof startTime !== 'string' || startTime === '') return {}
  const error = validateTimeRange(startTime, typeof endTime === 'string' && endTime !== '' ? endTime : null)
  if (error === 'END_BEFORE_START' || error === 'INVALID_END') return { endTime: `resources.scale_events.errors.${error.toLowerCase()}` }
  if (error === 'INVALID_START') return { startTime: 'resources.scale_events.errors.invalid_start' }
  return {}
}

const isEmpty = (value: unknown): boolean => value === null || value === undefined || value === ''

const validateRequired = (values: Record<string, unknown>): Record<string, string> => ({
  ...(isEmpty(values.startTime) && { startTime: 'ra.validation.required' }),
  ...(values.type === 'MARPOL' && isEmpty(values.marpolAnnex) && { marpolAnnex: 'ra.validation.required' }),
})

const hasStays = (events: readonly EventRecord[]): boolean => events.some(event => event.type === 'PLACE')

const validateStartWithinStay =
  (events: readonly EventRecord[] | undefined, storedStart: string | undefined) =>
  (values: Record<string, unknown>): Record<string, string> => {
    const { startTime } = values
    if (events === undefined || typeof startTime !== 'string') return {}
    const at = Date.parse(startTime)
    if (Number.isNaN(at) || (storedStart !== undefined && Date.parse(storedStart) === at)) return {}
    if (!hasStays(events)) return { startTime: 'resources.scale_events.errors.no_stays' }
    return isWithinAnyStay(events, at) ? {} : { startTime: 'resources.scale_events.errors.start_outside_stays' }
  }

export interface SpecialEventFormProps {
  defaultValues?: () => Record<string, unknown>
}

const useConnectionPointChoices = (scaleRecordId: unknown) => {
  const { data: points = [] } = useGetList<ConnectionPoint & { id: string }>('connection_points', {
    pagination: { page: 1, perPage: 1000 },
  })
  const events = useScaleEvents(scaleRecordId) ?? []
  const muelles = new Set(events.flatMap(event => (event.type === 'PLACE' ? [event.muelle] : [])))
  const atScale = muelles.size > 0 ? points.filter(point => muelles.has(point.muelle)) : points
  return [...atScale]
    .sort((a, b) => Number(a.status !== 'IN_SERVICE') - Number(b.status !== 'IN_SERVICE') || a.code.localeCompare(b.code))
    .map(point => ({
      id: point.code,
      name: point.status === 'IN_SERVICE' ? `${point.code} · ${point.terminal}` : `${point.code} · ${point.terminal} (${point.status})`,
    }))
}

const BombeoFields: FC<{ scaleRecordId: unknown; isNew: boolean }> = ({ scaleRecordId, isNew }) => {
  const translate = useTranslate()
  const connectionPoints = useConnectionPointChoices(scaleRecordId)
  const helper = isNew ? 'resources.scale_events.helpers.prefilled' : false
  return (
    <>
      <SelectInput
        source="connectionPoint"
        label="resources.scale_events.fields.connectionPoint"
        choices={connectionPoints}
        emptyText="—"
      />
      <SelectInput
        source="operationKind"
        label="resources.scale_events.fields.operationKind"
        choices={OPERATION_KINDS.map(kind => ({ id: kind, name: translate(`enums.OperationKind.${kind}`) }))}
        emptyText="—"
        helperText={helper}
      />
      <SelectInput
        source="productClass"
        label="resources.scale_events.fields.productClass"
        choices={PRODUCT_CLASSES.map(productClass => ({ id: productClass, name: translate(`enums.ProductClass.${productClass}`) }))}
        emptyText="—"
        helperText={helper}
      />
      <TextInput source="product" label="resources.scale_events.fields.product" helperText={helper} />
      <NumberInput source="quantityT" label="resources.scale_events.fields.quantityT" min={0} validate={minValue(0)} helperText={helper} />
    </>
  )
}

const MarpolFields: FC = () => {
  const translate = useTranslate()
  return (
    <SelectInput
      source="marpolAnnex"
      label="resources.scale_events.fields.marpolAnnex"
      choices={MARPOL_ANNEXES.map(annex => ({ id: annex, name: translate(`enums.MarpolAnnex.${annex}`) }))}
      isRequired
    />
  )
}

export const SpecialEventForm: FC<SpecialEventFormProps> = ({ defaultValues }) => {
  const translate = useTranslate()
  const record = useEventRecord()
  const events = useScaleEvents(record?.scaleRecordId)
  const storedStart = record?.id === undefined ? undefined : record.startTime
  const validateStart = validateStartWithinStay(events, storedStart)
  const validate = (values: Record<string, unknown>): Record<string, string> => ({
    ...validateRequired(values),
    ...validateStart(values),
    ...validateRange(values),
  })
  return (
    <SimpleForm
      validate={validate}
      defaultValues={defaultValues}
      toolbar={
        <Toolbar>
          <SaveButton />
        </Toolbar>
      }
    >
      {events !== undefined && !hasStays(events) && (
        <Alert severity="warning" data-testid="no-stays-notice">
          {translate('resources.scale_events.errors.no_stays')}
        </Alert>
      )}
      <TextInput source="scaleId" label="resources.scale_events.fields.scaleId" readOnly />
      <DateTimeInput
        source="startTime"
        label="resources.scale_events.fields.startTime"
        format={toDateTimeInput}
        parse={fromDateTimeInput}
        validate={required()}
      />
      <DateTimeInput
        source="endTime"
        label="resources.scale_events.fields.endTime"
        helperText="resources.scale_events.helpers.endTime"
        format={toDateTimeInput}
        parse={fromDateTimeInput}
      />
      <FormDataConsumer<{ scaleRecordId?: string; id?: string; type?: SpecialEventType }>>
        {({ formData }) =>
          formData.type === 'MARPOL' ? (
            <MarpolFields />
          ) : (
            <BombeoFields scaleRecordId={formData.scaleRecordId} isNew={formData.id === undefined} />
          )
        }
      </FormDataConsumer>
    </SimpleForm>
  )
}
